import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCardModule } from '@angular/material/card';

import { NotificationService } from '@shared/services/notification.service';
import { PageHeaderComponent } from '@shared/components/layout';
import { CellEditSaveEvent, FilterChip, TableColumn } from '@shared/components/data-table/data-table.component';
import { FilterMenuOption, FilterMenuSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { MinistryManagementService, MinistryDto, UpdateMinistryDto } from '../../services/ministry-management.service';

/**
 * Componente de listado de ministerios
 * Muestra una tabla con los ministerios disponibles y acciones para gestionar
 */
@Component({
  selector: 'app-ministry-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatTableModule,
    MatSlideToggleModule,
    MatCardModule,
    PageHeaderComponent,
    TableSurfaceComponent
  ],
  templateUrl: './ministry-list.component.html',
  styleUrl: './ministry-list.component.scss'
})
export class MinistryListComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [
    {
      key: 'name',
      header: 'Nombre',
      hideable: true,
      visible: true,
      resizable: true,
      editable: true,
      editType: 'text',
      exportValue: row => row.name ?? ''
    },
    {
      key: 'isActive',
      header: 'Activo',
      hideable: true,
      visible: true,
      resizable: true,
      exportValue: row => row.isActive ? 'Activo' : 'Inactivo'
    },
    { key: 'actions', header: 'Acciones', width: '1%', minWidth: '104px', exportable: false }
  ];
  tableData: MinistryDto[] = [];
  isLoading = false;
  enableDragDrop = true;
  savingCell?: { row: MinistryDto; column: string };
  activeFilterChips: FilterChip[] = [];
  statusFilterOptions: FilterMenuOption[] = this.buildStatusFilterOptions(true);
  
  filterForm: FormGroup;
  allMinistries: MinistryDto[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private service: MinistryManagementService,
    private dialog: MatDialog,
    private router: Router,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      searchTerm: [''],
      status: [true as boolean | null]
    });
  }

  ngOnInit(): void {
    this.loadMinistries();
    
    this.filterForm.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged((previous, current) =>
          previous.searchTerm === current.searchTerm && previous.status === current.status
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.applyFilter());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga todos los ministerios
   */
  private loadMinistries(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ministries: MinistryDto[]) => {
          this.allMinistries = ministries;
          this.applyFilter();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading ministries:', error);
          this.notificationService.error('Error al cargar los ministerios');
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Aplica los filtros a la tabla
   */
  private applyFilter(): void {
    const statusFilter = this.filterForm.get('status')?.value;
    const searchTerm = String(this.filterForm.get('searchTerm')?.value ?? '').trim().toLowerCase();
    
    let filteredData = this.allMinistries;
    
    if (statusFilter === true) {
      filteredData = this.allMinistries.filter(m => m.isActive);
    } else if (statusFilter === false) {
      filteredData = this.allMinistries.filter(m => !m.isActive);
    }

    if (searchTerm) {
      filteredData = filteredData.filter(m => (m.name ?? '').toLowerCase().includes(searchTerm));
    }
    
    this.tableData = [...filteredData];
    this.buildFilterChips();
    this.cdr.markForCheck();
  }

  private buildFilterChips(): void {
    const chips: FilterChip[] = [];
    const status = this.filterForm.get('status')?.value;

    if (status === true) {
      chips.push({ key: 'status', label: 'Activos', removable: true });
    } else if (status === false) {
      chips.push({ key: 'status', label: 'Inactivos', removable: true });
    }

    this.activeFilterChips = chips;
    this.statusFilterOptions = this.buildStatusFilterOptions(status);
  }

  private buildStatusFilterOptions(status: boolean | null): FilterMenuOption[] {
    return [
      { key: 'status', label: 'Todos', value: null, active: status === null },
      { key: 'status', label: 'Activos', value: true, active: status === true },
      { key: 'status', label: 'Inactivos', value: false, active: status === false },
    ];
  }

  /**
   * Navega al editor para crear un nuevo ministerio
   */
  createNew(): void {
    this.router.navigate(['/configuration/ministries/new']);
  }

  /**
   * Navega al editor para editar un ministerio existente
   */
  editMinistry(ministry: MinistryDto): void {
    this.router.navigate(['/configuration/ministries', ministry.id, 'edit']);
  }

  /**
   * Solicita confirmaciÃ³n para eliminar un ministerio
   */
  deleteMinistry(ministry: MinistryDto): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar Ministerio',
        message: `Â¿EstÃ¡s seguro de que deseas eliminar el ministerio "${ministry.name}"?`
      },
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.confirmDelete(ministry.id!);
        }
      });
  }

  /**
   * Confirma la eliminaciÃ³n de un ministerio
   */
  private confirmDelete(id: number): void {
    this.service.delete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Ministerio eliminado exitosamente');
          this.loadMinistries();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error deleting ministry:', error);
          this.notificationService.error((error.error as any)?.message || 'Error al eliminar el ministerio');
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Maneja el reordenamiento de ministerios
   */
  onMinistryReorder(event: any): void {
    const { data } = event;
    
    // Actualizar el sortOrder para cada item basado en su nueva posiciÃ³n
    data.forEach((ministry: MinistryDto, index: number) => {
      this.service.updateSortOrder(ministry.id!, index + 1)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (error: any) => {
            console.error('Error updating ministry sort order:', error);
            this.notificationService.error('Error al actualizar el orden');
            // Recargar los datos para revertir los cambios
            this.loadMinistries();
            this.cdr.markForCheck();
          }
        });
    });
    
    this.notificationService.success('Orden actualizado exitosamente');
  }

  /**
   * Maneja el cambio de estado (activo/inactivo) de un ministerio
   */
  onStatusChange(ministry: MinistryDto, newStatus: boolean): void {
    const updateDto = new UpdateMinistryDto({
      name: ministry.name,
      sortOrder: ministry.sortOrder,
      isActive: newStatus
    });

    this.service.update(ministry.id!, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          ministry.isActive = newStatus;
          this.notificationService.success(
            newStatus ? 'Ministerio activado exitosamente' : 'Ministerio desactivado exitosamente'
          );
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating ministry status:', error);
          this.notificationService.error('Error al actualizar el estado del ministerio');
          // Recargar los datos para revertir el cambio
          this.loadMinistries();
          this.cdr.markForCheck();
        }
      });
  }

  onToolbarSearch(term: string): void {
    this.filterForm.get('searchTerm')?.setValue(term);
  }

  onFilterRemove(key: string): void {
    if (key === 'status') {
      this.clearStatusFilter();
    }
  }

  onFilterOptionSelect(option: FilterMenuSelection): void {
    if (option.key === 'status') {
      this.filterForm.get('status')?.setValue(option.value, { emitEvent: false });
      this.applyFilter();
    }
  }

  clearStatusFilter(): void {
    this.filterForm.get('status')?.setValue(null, { emitEvent: false });
    this.applyFilter();
  }

  get statusFilterValue(): boolean | null {
    return this.filterForm.get('status')?.value ?? null;
  }

  onCellEditSave(event: CellEditSaveEvent<MinistryDto>): void {
    if (event.column !== 'name') {
      return;
    }

    const newName = String(event.newValue ?? '').trim();
    const oldName = String(event.oldValue ?? '');

    if (!newName) {
      this.notificationService.warning('El nombre del ministerio no puede quedar vacio');
      return;
    }

    if (newName === oldName) {
      return;
    }

    const ministry = event.row;
    this.savingCell = { row: ministry, column: event.column };
    this.cdr.markForCheck();

    const updateDto = new UpdateMinistryDto({
      name: newName,
      sortOrder: ministry.sortOrder,
      isActive: ministry.isActive
    });

    this.service.update(ministry.id!, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          ministry.name = newName;
          this.tableData = [...this.tableData];
          this.notificationService.success('Ministerio actualizado exitosamente');
          this.savingCell = undefined;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating ministry name:', error);
          ministry.name = oldName;
          this.notificationService.error('Error al actualizar el ministerio');
          this.savingCell = undefined;
          this.loadMinistries();
          this.cdr.markForCheck();
        }
      });
  }
}

